// =============================================================================
// SOCKET SERVICE
// =============================================================================
// WebSocket connection manager for real-time updates
// =============================================================================

import { io, Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '@chlann/shared';
import { api } from './client.js';

const SOCKET_URL = import.meta.env.VITE_API_URL || '';

// =============================================================================
// TYPES
// =============================================================================

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export type SocketConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

interface SocketServiceEvents {
  connectionStateChange: (state: SocketConnectionState) => void;
}

// =============================================================================
// SOCKET SERVICE CLASS
// =============================================================================

class SocketService {
  private socket: TypedSocket | null = null;
  private token: string | null = null;
  private connectionState: SocketConnectionState = 'disconnected';
  private listeners = new Map<keyof SocketServiceEvents, Set<(arg: unknown) => void>>();
  private joinedRooms = new Set<string>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private pendingListeners: Array<{ event: string; handler: (...args: any[]) => void }> = [];

  // ---------------------------------------------------------------------------
  // Connection Management
  // ---------------------------------------------------------------------------

  /**
   * Connect to the WebSocket server with authentication
   */
  connect(accessToken: string): void {
    // If already connected with same token, do nothing
    if (this.socket?.connected && this.token === accessToken) {
      return;
    }

    // Disconnect existing connection if any
    if (this.socket) {
      this.disconnect();
    }

    this.token = accessToken;
    this.setConnectionState('connecting');

    this.socket = io(SOCKET_URL, {
      // Evaluated on EVERY connection attempt, including automatic
      // reconnects. The previous static `auth: { token }` object replayed
      // the token captured at connect() forever: after the 15-minute JWT
      // expired during device sleep/backgrounding, every retry failed with
      // the same dead token while REST self-healed via its 401 -> refresh
      // path, so the app looked alive but received no realtime events.
      auth: (cb) => { cb({ token: api.getAccessToken() ?? this.token ?? '' }); },
      // Prefer WebSocket first; fall back to polling only if the proxy
      // blocks it. The previous order connected via polling then upgraded,
      // adding a round-trip at the start of every session.
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
    });

    this.setupEventHandlers();

    // Replay any listeners registered before socket was created
    for (const { event, handler } of this.pendingListeners) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.socket.on(event as any, handler);
    }
    this.pendingListeners = [];
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.token = null;
    this.joinedRooms.clear();
    this.setConnectionState('disconnected');
  }

  /**
   * Get current connection state
   */
  getConnectionState(): SocketConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get the raw socket instance for direct event handling
   */
  getSocket(): TypedSocket | null {
    return this.socket;
  }

  // ---------------------------------------------------------------------------
  // Event Handlers Setup
  // ---------------------------------------------------------------------------

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.setConnectionState('connected');

      // Rejoin rooms after reconnection
      for (const conversationId of this.joinedRooms) {
        this.socket?.emit('room:join', { conversationId });
      }
    });

    this.socket.on('disconnect', (_reason) => {
      this.setConnectionState('disconnected');
    });

    this.socket.on('connect_error', (error) => {
      this.setConnectionState('error');
      void this.maybeRefreshAuth(error);
    });
  }

  // Backend auth-middleware rejection messages (websocket/handlers.ts io.use).
  private static readonly AUTH_ERROR_MESSAGES = new Set([
    'Authentication required',
    'Invalid token',
    'User not found',
    'Authentication failed',
  ]);

  private authRefreshInFlight = false;
  private lastAuthRefreshAt = 0;
  // Floor between socket-triggered refreshes. Every refresh rotates the
  // single-use refresh cookie, so a server that kept rejecting fresh tokens
  // (clock skew, secret mismatch) must not churn rotations in a tight loop.
  private static readonly AUTH_REFRESH_COOLDOWN_MS = 10_000;

  /**
   * Self-heal an auth-rejected (re)connection. REST heals a dead JWT via its
   * 401 -> refresh interceptor, but a reconnecting socket has no REST call to
   * piggyback on - without this, a device waking from sleep retries forever
   * with the expired token. Refresh once, then kick the socket; the auth
   * callback above supplies the fresh token on that attempt.
   */
  private async maybeRefreshAuth(error: Error): Promise<void> {
    if (!SocketService.AUTH_ERROR_MESSAGES.has(error.message)) return;
    if (this.authRefreshInFlight) return;
    if (Date.now() - this.lastAuthRefreshAt < SocketService.AUTH_REFRESH_COOLDOWN_MS) return;

    this.authRefreshInFlight = true;
    this.lastAuthRefreshAt = Date.now();
    try {
      const refreshed = await api.refreshAccessToken();
      // Middleware rejections don't reliably re-enter the manager's retry
      // loop; reconnect explicitly. No-op if a retry already succeeded.
      if (refreshed && this.socket && !this.socket.connected) {
        this.socket.connect();
      }
      // If the refresh failed the refresh cookie is dead too - nothing to
      // retry with; the auth store's next initialize() handles sign-out.
    } finally {
      this.authRefreshInFlight = false;
    }
  }

  private setConnectionState(state: SocketConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.emit('connectionStateChange', state);
    }
  }

  // ---------------------------------------------------------------------------
  // Room Management
  // ---------------------------------------------------------------------------

  /**
   * Join a conversation room for real-time updates.
   * If not yet connected, the room is queued and joined on connect.
   */
  joinRoom(conversationId: string): void {
    if (this.joinedRooms.has(conversationId)) return;

    this.joinedRooms.add(conversationId);

    if (this.socket?.connected) {
      this.socket.emit('room:join', { conversationId });
    }
  }

  /**
   * Leave a conversation room
   */
  leaveRoom(conversationId: string): void {
    if (!this.socket?.connected) return;

    if (this.joinedRooms.has(conversationId)) {
      this.socket.emit('room:leave', { conversationId });
      this.joinedRooms.delete(conversationId);
    }
  }

  /**
   * Get list of joined rooms
   */
  getJoinedRooms(): string[] {
    return Array.from(this.joinedRooms);
  }

  // ---------------------------------------------------------------------------
  // Typing Indicators
  // ---------------------------------------------------------------------------

  /**
   * Send typing start event
   */
  startTyping(conversationId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('typing:start', { conversationId });
  }

  /**
   * Send typing stop event
   */
  stopTyping(conversationId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('typing:stop', { conversationId });
  }

  // ---------------------------------------------------------------------------
  // Message Events
  // ---------------------------------------------------------------------------

  /**
   * Mark a message as delivered
   */
  markDelivered(messageId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('message:delivered', { messageId });
  }

  /**
   * Mark messages as read
   */
  markRead(conversationId: string, messageIds: string[]): void {
    if (!this.socket?.connected) return;
    this.socket.emit('message:read', { conversationId, messageIds });
  }

  // ---------------------------------------------------------------------------
  // Call Signaling
  // ---------------------------------------------------------------------------

  /**
   * Send SDP offer or answer for WebRTC
   */
  sendSDP(data: {
    callId: string;
    toUserId: string;
    type: 'offer' | 'answer';
    sdp: string;
  }): void {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit('call:sdp', {
      callId: data.callId,
      toUserId: data.toUserId,
      type: data.type,
      sdp: data.sdp,
      fromUserId: '', // Server fills this from auth
    });
  }

  /**
   * Send ICE candidate for WebRTC
   */
  sendICECandidate(data: {
    callId: string;
    toUserId: string;
    candidate: RTCIceCandidateInit;
  }): void {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit('call:ice-candidate', {
      callId: data.callId,
      toUserId: data.toUserId,
      candidate: data.candidate,
      fromUserId: '', // Server fills this from auth
    });
  }

  /**
   * Accept an incoming call via WebSocket
   */
  acceptCallSocket(callId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('call:accept', { callId });
  }

  /**
   * Decline an incoming call via WebSocket
   */
  declineCallSocket(callId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('call:decline', { callId });
  }

  /**
   * Leave/end a call via WebSocket
   */
  leaveCall(callId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('call:leave', { callId });
  }

  /**
   * Toggle video state via WebSocket
   */
  toggleVideoSocket(callId: string, enabled: boolean): void {
    if (!this.socket?.connected) return;
    this.socket.emit('call:toggle-video', { callId, enabled });
  }

  /**
   * Toggle audio state via WebSocket
   */
  toggleAudioSocket(callId: string, enabled: boolean): void {
    if (!this.socket?.connected) return;
    this.socket.emit('call:toggle-audio', { callId, enabled });
  }

  /**
   * Send an ephemeral in-call reaction (tap-to-react emoji burst). Relayed by
   * the server to the rest of the call room; never persisted.
   */
  sendCallReaction(callId: string, emoji: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('call:reaction', { callId, emoji });
  }

  // ---------------------------------------------------------------------------
  // Server Event Listeners
  // ---------------------------------------------------------------------------

  /**
   * Listen for a server event
   */
  on<E extends keyof ServerToClientEvents>(
    event: E,
    handler: ServerToClientEvents[E]
  ): () => void {
    if (!this.socket) {
      // Queue for replay when socket is created
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.pendingListeners.push({ event, handler: handler as any });
      return () => {
        this.pendingListeners = this.pendingListeners.filter(
          (l) => !(l.event === event && l.handler === handler)
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.socket?.off(event, handler as any);
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on(event, handler as any);

    // Return cleanup function
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.socket?.off(event, handler as any);
    };
  }

  /**
   * Remove a server event listener
   */
  off<E extends keyof ServerToClientEvents>(
    event: E,
    handler: ServerToClientEvents[E]
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket?.off(event, handler as any);
  }

  // ---------------------------------------------------------------------------
  // Internal Event Emitter
  // ---------------------------------------------------------------------------

  private emit<E extends keyof SocketServiceEvents>(
    event: E,
    data: Parameters<SocketServiceEvents[E]>[0]
  ): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach((handler) => { handler(data); });
    }
  }

  /**
   * Subscribe to socket service events (connection state changes, etc.)
   */
  subscribe<E extends keyof SocketServiceEvents>(
    event: E,
    handler: SocketServiceEvents[E]
  ): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler as (arg: unknown) => void);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(handler as (arg: unknown) => void);
    };
  }
}

// =============================================================================
// EXPORT SINGLETON
// =============================================================================

export const socketService = new SocketService();
